import { useDemoSafeMutation } from '@/hooks/useDemoGuard';
import type { Json } from '@/integrations/supabase/types';
import { notificationsRepository } from '@/repositories/notifications';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useAuth } from './useAuth';
import { useRealtimeRows } from './useRealtimeRows';

// The `[key: string]` index signature declares this is a JSON-shaped
// Postgres row, which is what `useRealtimeRows<Notification>` needs to
// forward the type into Supabase's `.on<T>(…)` — Supabase constrains T
// to a JSON-row shape. Every concrete field below already widens to
// `Json | undefined`, so the index signature is honest, not a back door.
export interface Notification {
  id: string;
  company_id: string;
  user_id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  category:
    | 'general'
    | 'payment'
    | 'maintenance'
    | 'contract'
    | 'task'
    | 'invitation';
  is_read: boolean;
  link: string | null;
  metadata: Json | null;
  created_at: string;
  [key: string]: Json | undefined;
}

export function useNotifications() {
  const { user, companyId } = useAuth();
  const queryClient = useQueryClient();

  // Fetch notifications
  const {
    data: notifications = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['notifications', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const data = await notificationsRepository.listForUser(user.id);
      return data as Notification[];
    },
    enabled: !!user?.id,
  });

  // Real-time subscription — every INSERT for this user refetches the
  // notifications list. Embedding the user id in the channel name means
  // concurrent tabs (or a user account switch within one tab) get their
  // own isolated channel instead of stomping on a shared one.
  useRealtimeRows<Notification>({
    enabled: !!user?.id,
    channelName: `notifications:user:${user?.id ?? 'anon'}`,
    table: 'notifications',
    filter: user?.id ? `user_id=eq.${user.id}` : undefined,
    onInsert: () => {
      void refetch();
    },
  });

  // Mark as read
  const markAsRead = useDemoSafeMutation({
    mutationFn: async (notificationId: string) => {
      if (!companyId) return;

      await notificationsRepository.markAsRead(notificationId, companyId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Mark all as read
  const markAllAsRead = useDemoSafeMutation({
    mutationFn: async () => {
      if (!user?.id || !companyId) return;

      await notificationsRepository.markAllAsRead(user.id, companyId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Delete notification
  const deleteNotification = useDemoSafeMutation({
    mutationFn: async (notificationId: string) => {
      if (!companyId) return;

      await notificationsRepository.remove(notificationId, companyId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // NOTE: `createNotification` was removed in the notification refactor.
  // Direct frontend INSERTs into `public.notifications` would double-fire
  // alongside the DB trigger that already enriches metadata.eventKey +
  // audience and routes the row through the Nest dispatcher. Use
  // `POST /notifications/dispatch` instead for explicit sends.

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  // Update PWA App Badge with unread count
  useEffect(() => {
    if ('setAppBadge' in navigator) {
      if (unreadCount > 0) {
        navigator.setAppBadge(unreadCount).catch(() => {});
      } else {
        navigator.clearAppBadge?.().catch(() => {});
      }
    }
  }, [unreadCount]);

  return {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  };
}
